import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { AdjuntoMensaje, MensajeParaResidente, Pagina } from '@kodica/shared';

import isotipo from '../assets/marca/kodica-isotipo.svg';
import { Iconos, type NombreIcono } from '../componentes/iconos';
import { Boton, Campo, Chip, Entrada, ErrorCaja, Panel } from '../componentes/ui';
import { almacenToken, ErrorApi, pedir } from '../lib/api';
import { useAlLlegarAviso } from '../lib/avisos';
import { compartirImagen } from '../lib/compartir';
import { qrConLogo } from '../lib/qr-invitacion';
import { textoInvitacion } from '../lib/texto-invitacion';
import { cuando } from '../lib/fechas';
import { conLigas } from '../lib/ligas';
import { consulta } from '../lib/consultas';
import { baseApi } from '../lib/servidor';
import { versionLarga } from '../lib/version';
import { useSesion } from '../lib/sesion';

/**
 * La app del **residente**, pensada para teléfono en vertical y una sola mano.
 *
 * Un tablero con tres accesos —generar un código, ver quién entró y ver los
 * códigos emitidos— en vez de una sola tira larga: el residente entra a hacer
 * una cosa concreta y no a leer todo.
 *
 * Cada sección es una **dirección propia** (`/mi-vivienda/generar`…). No es por
 * poder compartir enlaces: es lo que le da historial al botón atrás de Android,
 * que si no cierra la app desde cualquier pantalla en vez de volver al tablero.
 *
 * El QR se dibuja en el propio teléfono a partir del token firmado que devolvió
 * el servidor; no se pide una imagen. Ver `TarjetaQr`.
 */

type Vista = 'generar' | 'ingresos' | 'codigos' | 'avisos';

export function ResidentePagina() {
  const { usuario, cerrar } = useSesion();
  const navegar = useNavigate();
  const { pathname } = useLocation();
  const enInicio = pathname.replace(/\/+$/, '') === '/mi-vivienda';

  return (
    // El ancho crece por tramos y no de golpe. Esta vista se escribió para el
    // pulgar —una pila vertical de tarjetas— y estirarla a 1920 daría renglones
    // de un metro. Lo que se gana con sitio es poner dos tarjetas al lado, no
    // hacerlas más largas.
    <div className="mx-auto min-h-full w-full max-w-md px-4 pb-16 md:max-w-3xl lg:max-w-5xl">
      <header className="flex items-center justify-between py-5">
        {enInicio ? (
          <div className="flex items-center gap-3">
            {/* `alt` vacío a propósito: el nombre va escrito al lado y un lector
                de pantalla lo diría dos veces. */}
            <img src={isotipo} alt="" className="h-10 w-auto shrink-0" />
            <div>
              <p className="text-headline-md text-primary">KODICA</p>
              <p className="text-body-md text-on-surface-variant">
                {usuario?.nombre ?? 'Mi vivienda'}
              </p>
            </div>
          </div>
        ) : (
          // `-1` y no la ruta del tablero: así el botón de la pantalla y el del
          // teléfono hacen exactamente lo mismo.
          <Boton variante="texto" icono={Iconos.anterior} onClick={() => navegar(-1)}>
            Inicio
          </Boton>
        )}
        {/* Junto a «Salir», que es donde el residente ya sabe que están las
            cosas de su cuenta. En el teléfono no hay barra lateral donde
            colgarlo. */}
        <Boton variante="texto" onClick={() => navegar('/contrasena')}>
          Contraseña
        </Boton>
        <Boton variante="texto" onClick={cerrar}>
          Salir
        </Boton>
      </header>

      <Routes>
        <Route index element={<Inicio ir={(v) => navegar(v)} />} />
        <Route path="generar" element={<Generar />} />
        <Route path="ingresos" element={<Ingresos />} />
        <Route path="codigos" element={<Codigos />} />
        <Route path="avisos" element={<AvisosDelResidente />} />
      </Routes>
    </div>
  );
}

// ── Tablero ───────────────────────────────────────────────────

function Inicio({ ir }: { ir: (v: Vista) => void }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {/* Primero, y con su contador. Es lo unico de esta pantalla que **otro**
          decidio que el residente tiene que leer; lo demas lo abre el cuando
          quiere. */}
      <TarjetaAvisos alAbrir={() => ir('avisos')} />

      <TarjetaAccion
        icono="credencial"
        titulo="Generar código de ingreso"
        descripcion="Crea un QR para tu visita"
        onClick={() => ir('generar')}
      />
      <TarjetaAccion
        icono="entrar"
        titulo="Historial de ingresos"
        descripcion="Quién ha entrado a tu vivienda"
        onClick={() => ir('ingresos')}
      />
      <TarjetaAccion
        icono="etiqueta"
        titulo="Historial de códigos"
        descripcion="Las invitaciones que has generado"
        onClick={() => ir('codigos')}
      />
      {/* Los avisos ocupan el ancho entero: es un bloque de texto, no una
          acción, y partido en media columna se lee peor. */}
      <div className="md:col-span-2">
        <Avisos />
      </div>

      {/* La versión, **también estando dentro**.
          
          Ya estaba en el login, pero ahí solo la ve quien no ha entrado — y el
          estado normal de un residente es haber entrado y quedarse. Cuando la
          app pedía una dirección que el servidor ya no tenía, ni él ni nosotros
          pudimos comprobar qué versión traía el teléfono sin cerrar sesión: el
          único sitio donde se decía era justo el que ya había pasado. */}
      <p className="md:col-span-2 text-center text-label-sm text-outline">
        Kodica {versionLarga()}
      </p>
    </div>
  );
}

function TarjetaAccion({
  icono,
  titulo,
  descripcion,
  insignia,
  onClick,
}: {
  icono: NombreIcono;
  titulo: string;
  /** Opcional: hay tarjetas que se explican solas con su título. */
  descripcion?: string;
  /**
   * Cuántas cosas sin atender. Se pinta como el globito rojo de cualquier app
   * de mensajes — un número dentro de un círculo — porque es lo que la gente ya
   * sabe leer sin que nadie se lo explique.
   */
  insignia?: number;
  onClick: () => void;
}) {
  const Icono = Iconos[icono];
  return (
    <button onClick={onClick} className="w-full text-left">
      <Panel className="transition-colors hover:bg-surface-container-low">
        <div className="flex items-center gap-4 p-5">
          <span className="rounded-full bg-primary-container/20 p-3 text-primary">
            <Icono tam={28} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-headline-md text-on-surface">{titulo}</p>
            {descripcion && <p className="text-body-md text-on-surface-variant">{descripcion}</p>}
          </div>

          {/* Se corta en 99 como en todas partes: a partir de ahí el número
              exacto no cambia lo que la persona va a hacer, y ensancharía el
              círculo hasta deformar la fila. */}
          {insignia !== undefined && insignia > 0 && (
            <span
              aria-label={`${insignia} sin leer`}
              className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-error px-1.5 text-label-sm font-bold text-on-error"
            >
              {insignia > 99 ? '99+' : insignia}
            </span>
          )}

          <Iconos.siguiente tam={24} className="shrink-0 text-outline" />
        </div>
      </Panel>
    </button>
  );
}

// ── Generar código ────────────────────────────────────────────

type Modo = 'UNICO' | 'RANGO' | 'RECURRENTE';

/** Fin del día de mañana en formato datetime-local ("YYYY-MM-DDTHH:MM"). */
function finDeManana(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(23, 59, 0, 0);
  return aLocalInput(d);
}

function aLocalInput(d: Date): string {
  // datetime-local no lleva zona; se arma con la hora local del teléfono.
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** datetime-local → ISO con zona (lo que espera el API). */
function aIso(local: string): string {
  return new Date(local).toISOString();
}

interface Invitacion {
  id: string;
  token: string;
  /** La vivienda que autoriza. La nombra el texto que se comparte. */
  unidad: string;
  /** Los límites del reglamento, que ese mismo texto recuerda. */
  limiteVelocidadCamellon: number;
  limiteVelocidadCalles: number;
  nombreInvitado: string | null;
  estado: 'ACTIVA' | 'USADA' | 'EXPIRADA' | 'CANCELADA';
  usosMaximos: number;
  usosRealizados: number;
  vigenteHasta: string;
  creadoEn: string;
  diasSemana?: number[];
  horaInicio?: string | null;
  horaFin?: string | null;
}

const DIAS_CORTOS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const DIAS_NOMBRE = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

/** "Martes y jueves, 9:00–14:00" — el resumen que ve el residente. */
function resumenRecurrencia(i: Invitacion): string | null {
  const dias = i.diasSemana ?? [];
  const partes: string[] = [];
  if (dias.length > 0) {
    const nombres = dias
      .slice()
      .sort((a, b) => a - b)
      .map((d) => DIAS_NOMBRE[d] ?? '');
    partes.push(
      nombres.length === 1
        ? nombres[0]!
        : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`,
    );
  }
  if (i.horaInicio && i.horaFin) partes.push(`${i.horaInicio}–${i.horaFin}`);
  if (partes.length === 0) return null;
  const texto = partes.join(', ');
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/**
 * Generar un código.
 *
 * **Se queda estrecho aunque haya sitio.** Es un formulario, y un formulario
 * ancho obliga al ojo a recorrer de un extremo a otro entre la etiqueta y el
 * campo. Las listas ganan con el ancho; esto no.
 */
function Generar() {
  const [modo, setModo] = useState<Modo>('UNICO');
  const [nombre, setNombre] = useState('');
  const [vence, setVence] = useState(finDeManana());
  const [desde, setDesde] = useState(aLocalInput(new Date()));
  const [hasta, setHasta] = useState(finDeManana());
  const [usos, setUsos] = useState(5);
  const [dias, setDias] = useState<number[]>([]);
  const [horaInicio, setHoraInicio] = useState('09:00');
  const [horaFin, setHoraFin] = useState('14:00');

  const [emitiendo, setEmitiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reciente, setReciente] = useState<Invitacion | null>(null);

  function alternarDia(d: number) {
    setDias((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  }

  async function crear() {
    // El nombre dejó de ser opcional: el guardia lo lee en voz alta cuando
    // el visitante llega, y va dentro del texto que se comparte. Se avisa
    // aquí y no se deja al servidor porque el error del API llega después
    // de un viaje de red, y este se puede dar de inmediato.
    if (!nombre.trim()) {
      setError('El nombre de la visita es obligatorio.');
      return;
    }
    if (modo === 'RECURRENTE' && dias.length === 0) {
      setError('Elige al menos un día de la semana.');
      return;
    }
    setEmitiendo(true);
    setError(null);
    try {
      const base = { nombreInvitado: nombre.trim() };
      const cuerpo =
        modo === 'UNICO'
          ? { ...base, vigenteHasta: aIso(vence), usosMaximos: 1 }
          : modo === 'RANGO'
            ? {
                ...base,
                vigenteDesde: aIso(desde),
                vigenteHasta: aIso(hasta),
                usosMaximos: usos,
              }
            : {
                ...base,
                vigenteDesde: aIso(desde),
                vigenteHasta: aIso(hasta),
                usosMaximos: usos,
                diasSemana: dias,
                horaInicio,
                horaFin,
              };
      const nueva = await pedir<Invitacion>('/invitaciones', {
        metodo: 'POST',
        cuerpo,
      });
      setReciente({ ...nueva, estado: 'ACTIVA', usosRealizados: 0 });
      setNombre('');
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo crear el código');
    } finally {
      setEmitiendo(false);
    }
  }

  if (reciente) {
    return (
      <div className="max-w-md space-y-4">
        <TarjetaQr invitacion={reciente} />
        <Boton variante="contorno" className="w-full" onClick={() => setReciente(null)}>
          Generar otro
        </Boton>
      </div>
    );
  }

  return (
    <div className="max-w-md space-y-4">
      <h1 className="text-headline-lg text-on-surface">Generar código de ingreso</h1>

      {error && <ErrorCaja>{error}</ErrorCaja>}

      <div className="grid grid-cols-3 gap-2">
        <BotonModo activo={modo === 'UNICO'} onClick={() => setModo('UNICO')}>
          Un solo uso
        </BotonModo>
        <BotonModo activo={modo === 'RANGO'} onClick={() => setModo('RANGO')}>
          Por fechas
        </BotonModo>
        <BotonModo activo={modo === 'RECURRENTE'} onClick={() => setModo('RECURRENTE')}>
          Días fijos
        </BotonModo>
      </div>

      <Panel>
        <div className="space-y-4 p-5">
          <Campo
            etiqueta="¿A quién esperas?"
            ayuda="Va en el mensaje que le mandas y el guardia lo lee al llegar"
          >
            <Entrada
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Nombre de tu visita"
              autoComplete="off"
            />
          </Campo>

          {modo === 'UNICO' && (
            <Campo etiqueta="Vence" ayuda="Sirve una sola vez, hasta esta fecha y hora">
              <Entrada
                type="datetime-local"
                value={vence}
                onChange={(e) => setVence(e.target.value)}
              />
            </Campo>
          )}

          {modo !== 'UNICO' && (
            <>
              <Campo etiqueta="Válido desde">
                <Entrada
                  type="datetime-local"
                  value={desde}
                  onChange={(e) => setDesde(e.target.value)}
                />
              </Campo>
              <Campo etiqueta="Válido hasta">
                <Entrada
                  type="datetime-local"
                  value={hasta}
                  onChange={(e) => setHasta(e.target.value)}
                />
              </Campo>
            </>
          )}

          {modo === 'RECURRENTE' && (
            <>
              <Campo etiqueta="Qué días" ayuda="Solo estos días abrirá el código">
                <div className="mt-1 flex gap-1.5">
                  {DIAS_CORTOS.map((letra, d) => (
                    <button
                      key={d}
                      type="button"
                      aria-pressed={dias.includes(d)}
                      aria-label={DIAS_NOMBRE[d]}
                      onClick={() => alternarDia(d)}
                      className={`toque h-11 flex-1 rounded-lg text-label-bold transition-all ${
                        dias.includes(d)
                          ? 'bg-primary text-on-primary'
                          : 'border border-outline-variant bg-surface-container-lowest text-on-surface-variant'
                      }`}
                    >
                      {letra}
                    </button>
                  ))}
                </div>
              </Campo>
              <div className="grid grid-cols-2 gap-3">
                <Campo etiqueta="Desde las">
                  <Entrada
                    type="time"
                    value={horaInicio}
                    onChange={(e) => setHoraInicio(e.target.value)}
                  />
                </Campo>
                <Campo etiqueta="Hasta las">
                  <Entrada
                    type="time"
                    value={horaFin}
                    onChange={(e) => setHoraFin(e.target.value)}
                  />
                </Campo>
              </div>
            </>
          )}

          {modo !== 'UNICO' && (
            <Campo etiqueta="Cuántas veces sirve" ayuda="Entre 1 y 50">
              <Entrada
                type="number"
                min={1}
                max={50}
                value={usos}
                onChange={(e) => setUsos(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
              />
            </Campo>
          )}

          <Boton
            variante="primario"
            className="w-full"
            disabled={emitiendo}
            onClick={() => void crear()}
          >
            {emitiendo ? 'Creando…' : 'Crear código'}
          </Boton>
        </div>
      </Panel>
    </div>
  );
}

function BotonModo({
  activo,
  onClick,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`toque rounded-lg px-4 py-3 text-label-bold transition-all ${
        activo
          ? 'bg-secondary-container text-on-secondary-container'
          : 'border border-outline-variant bg-surface-container-lowest text-on-surface-variant'
      }`}
    >
      {children}
    </button>
  );
}

// ── Historial genérico (paginado + filtro por fechas) ─────────

interface Historial<T> {
  datos: Pagina<T> | null;
  error: string | null;
  cargando: boolean;
  pagina: number;
  irA: (n: number) => void;
  desde: string;
  hasta: string;
  setDesde: (v: string) => void;
  setHasta: (v: string) => void;
  recargar: () => void;
}

function useHistorial<T>(base: string): Historial<T> {
  const [pagina, setPagina] = useState(1);
  const [desde, setDesdeRaw] = useState('');
  const [hasta, setHastaRaw] = useState('');
  const [datos, setDatos] = useState<Pagina<T> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(() => {
    setCargando(true);
    const ruta = consulta(base, {
      pagina,
      porPagina: 10,
      desde: desde ? new Date(`${desde}T00:00:00`).toISOString() : undefined,
      hasta: hasta ? new Date(`${hasta}T23:59:59`).toISOString() : undefined,
    });
    pedir<Pagina<T>>(ruta)
      .then((r) => {
        setDatos(r);
        setError(null);
      })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo cargar'))
      .finally(() => setCargando(false));
  }, [base, pagina, desde, hasta]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Cambiar el filtro vuelve a la primera página: la 3 del filtro viejo casi
  // nunca existe en el nuevo.
  const setDesde = (v: string) => {
    setPagina(1);
    setDesdeRaw(v);
  };
  const setHasta = (v: string) => {
    setPagina(1);
    setHastaRaw(v);
  };

  return {
    datos,
    error,
    cargando,
    pagina,
    irA: setPagina,
    desde,
    hasta,
    setDesde,
    setHasta,
    recargar: cargar,
  };
}

function FiltroFechas({ h }: { h: Historial<unknown> }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <Campo etiqueta="Desde">
        <Entrada type="date" value={h.desde} onChange={(e) => h.setDesde(e.target.value)} />
      </Campo>
      <Campo etiqueta="Hasta">
        <Entrada type="date" value={h.hasta} onChange={(e) => h.setHasta(e.target.value)} />
      </Campo>
    </div>
  );
}

function Paginador({ h }: { h: Historial<unknown> }) {
  const p = h.datos;
  if (!p || p.total === 0) return null;
  return (
    <div className="flex items-center justify-between pt-2">
      <Boton
        variante="texto"
        icono={Iconos.anterior}
        disabled={p.pagina <= 1}
        onClick={() => h.irA(p.pagina - 1)}
      >
        Anterior
      </Boton>
      <span className="text-body-md text-on-surface-variant">
        {p.pagina} de {p.paginas}
      </span>
      <Boton variante="texto" disabled={p.pagina >= p.paginas} onClick={() => h.irA(p.pagina + 1)}>
        Siguiente
      </Boton>
    </div>
  );
}

// ── Historial de ingresos ─────────────────────────────────────

interface Ingreso {
  id: string;
  nombreVisitante: string | null;
  entradaEn: string;
  estado: string;
  /** Solo en los pasos de residente: si iba entrando o saliendo. */
  sentido: 'ENTRADA' | 'SALIDA' | null;
  origen: string;
  tipoVisita: string | null;
  fotoRostroId: string | null;
}

const ORIGEN_TEXTO: Record<string, string> = {
  CASETA: 'Caseta',
  KIOSCO_QR: 'Código QR',
  TAG_UHF: 'Tag',
  FACIAL: 'Reconocimiento facial',
  MANUAL: 'Manual',
};

function Ingresos() {
  const h = useHistorial<Ingreso>('/historial/mi-vivienda');
  const [abierto, setAbierto] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <h1 className="text-headline-lg text-on-surface">Historial de ingresos</h1>
      <FiltroFechas h={h} />

      {h.error && <ErrorCaja>{h.error}</ErrorCaja>}
      {h.cargando && !h.datos && <p className="text-body-md text-on-surface-variant">Cargando…</p>}
      {h.datos?.total === 0 && (
        <p className="text-body-md text-on-surface-variant">
          No hay ingresos{h.desde || h.hasta ? ' en esas fechas' : ' todavía'}.
        </p>
      )}

      {/* Dos por fila en pantalla grande. Son tarjetas de altura parecida, y
          apiladas en una columna sobre un monitor se recorre el doble para ver
          lo mismo. */}
      <div className="grid gap-3 md:grid-cols-2">
        {(h.datos?.datos ?? []).map((i) => (
          <Panel key={i.id}>
            <button
              className="w-full p-4 text-left"
              onClick={() => setAbierto(abierto === i.id ? null : i.id)}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-body-md text-on-surface">
                    {i.nombreVisitante || 'Visita sin nombre'}
                  </p>
                  <p className="text-body-md text-on-surface-variant">
                    {new Date(i.entradaEn).toLocaleString('es-MX', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}{' '}
                    · {ORIGEN_TEXTO[i.origen] ?? i.origen.toLowerCase()}
                  </p>
                </div>
                <Chip tono={i.estado === 'DENTRO' ? 'ok' : 'neutro'}>
                  {i.estado === 'DENTRO'
                    ? 'Entró'
                    : i.estado === 'SALIO'
                      ? 'Salió'
                      : i.estado === 'REGISTRADO'
                        ? i.sentido === 'SALIDA'
                          ? 'Salió'
                          : 'Entró'
                        : 'En revisión'}
                </Chip>
              </div>
            </button>
            {abierto === i.id && <FotoIngreso fotoId={i.fotoRostroId} />}
          </Panel>
        ))}
      </div>

      <Paginador h={h} />
    </div>
  );
}

/** Pide la foto al abrir el ingreso, no antes. */
function FotoIngreso({ fotoId }: { fotoId: string | null }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!fotoId) return;
    let vivo = true;
    let objeto: string | null = null;
    void fetch(`${baseApi()}/api/historial/fotos/${fotoId}`, {
      headers: { Authorization: `Bearer ${almacenToken.leer() ?? ''}` },
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => {
        if (!b || !vivo) return;
        objeto = URL.createObjectURL(b);
        setUrl(objeto);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
      if (objeto) URL.revokeObjectURL(objeto);
    };
  }, [fotoId]);

  if (!fotoId) {
    return (
      <p className="border-t border-outline-variant p-4 text-body-md text-on-surface-variant">
        Esta visita no tiene foto.
      </p>
    );
  }
  return (
    <div className="border-t border-outline-variant p-4">
      {url ? (
        <img src={url} alt="Foto de la visita" className="w-full rounded-xl" />
      ) : (
        <div className="h-40 animate-pulse rounded-xl bg-surface-container-high" />
      )}
    </div>
  );
}

// ── Historial de códigos ──────────────────────────────────────

function Codigos() {
  const h = useHistorial<Invitacion>('/invitaciones');
  const [abierto, setAbierto] = useState<string | null>(null);

  async function cancelar(id: string) {
    try {
      await pedir(`/invitaciones/${id}`, { metodo: 'DELETE' });
      h.recargar();
    } catch {
      /* el estado se refresca solo; un fallo se ve al recargar */
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-headline-lg text-on-surface">Historial de códigos</h1>
      <FiltroFechas h={h} />

      {h.error && <ErrorCaja>{h.error}</ErrorCaja>}
      {h.cargando && !h.datos && <p className="text-body-md text-on-surface-variant">Cargando…</p>}
      {h.datos?.total === 0 && (
        <p className="text-body-md text-on-surface-variant">
          No hay códigos{h.desde || h.hasta ? ' en esas fechas' : ' todavía'}.
        </p>
      )}

      {/* Dos por fila en pantalla grande. Son tarjetas de altura parecida, y
          apiladas en una columna sobre un monitor se recorre el doble para ver
          lo mismo. */}
      <div className="grid gap-3 md:grid-cols-2">
        {(h.datos?.datos ?? []).map((inv) => (
          <FilaCodigo
            key={inv.id}
            invitacion={inv}
            abierto={abierto === inv.id}
            alAlternar={() => setAbierto(abierto === inv.id ? null : inv.id)}
            alCancelar={() => void cancelar(inv.id)}
          />
        ))}
      </div>

      <Paginador h={h} />
    </div>
  );
}

const TONO_ESTADO = {
  ACTIVA: 'ok',
  USADA: 'neutro',
  EXPIRADA: 'neutro',
  CANCELADA: 'malo',
} as const;
const TEXTO_ESTADO = {
  ACTIVA: 'Activa',
  USADA: 'Ya entró',
  EXPIRADA: 'Vencida',
  CANCELADA: 'Cancelada',
};

function FilaCodigo({
  invitacion,
  abierto,
  alAlternar,
  alCancelar,
}: {
  invitacion: Invitacion;
  abierto: boolean;
  alAlternar: () => void;
  alCancelar: () => void;
}) {
  return (
    <Panel>
      <button className="w-full p-4 text-left" onClick={alAlternar}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-body-md text-on-surface">
              {invitacion.nombreInvitado || 'Sin nombre'}
            </p>
            <p className="text-body-md text-on-surface-variant">
              {new Date(invitacion.creadoEn).toLocaleDateString('es-MX', {
                day: 'numeric',
                month: 'short',
              })}{' '}
              · {invitacion.usosRealizados}/{invitacion.usosMaximos} usos
            </p>
            {resumenRecurrencia(invitacion) && (
              <p className="truncate text-label-sm text-primary">
                {resumenRecurrencia(invitacion)}
              </p>
            )}
          </div>
          <Chip tono={TONO_ESTADO[invitacion.estado]}>{TEXTO_ESTADO[invitacion.estado]}</Chip>
        </div>
      </button>
      {abierto && (
        <div className="border-t border-outline-variant p-4">
          <TarjetaQr
            invitacion={invitacion}
            alCancelar={invitacion.estado === 'ACTIVA' ? alCancelar : undefined}
          />
        </div>
      )}
    </Panel>
  );
}

// ── QR grande, listo para enseñar o compartir ─────────────────

function TarjetaQr({
  invitacion,
  alCancelar,
}: {
  invitacion: Invitacion;
  alCancelar?: () => void;
}) {
  const [imagen, setImagen] = useState<string | null>(null);

  useEffect(() => {
    // El código lleva la K en el centro, y con eso sube la correccion de
    // errores: lo que cuesta y por que sale a cuenta esta medido en
    // `qrConLogo`. Si algo falla se queda sin imagen y el boton de
    // compartir desactivado, que es mejor que compartir un QR a medias.
    void qrConLogo(invitacion.token)
      .then(setImagen)
      .catch(() => setImagen(null));
  }, [invitacion.token]);

  const [compartiendo, setCompartiendo] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  async function compartir() {
    if (!imagen) return;
    setCompartiendo(true);
    setAviso(null);
    try {
      const resultado = await compartirImagen(imagen, {
        titulo: 'Invitación de Kodica',
        texto: textoInvitacion(invitacion),
        archivo: `invitacion-kodica-${invitacion.id}.png`,
      });
      if (resultado === 'descargado') {
        setAviso('Se descargó la imagen del código. Mándasela a tu invitado.');
      }
    } catch (e) {
      setAviso(e instanceof Error ? `No se pudo compartir: ${e.message}` : 'No se pudo compartir.');
    } finally {
      setCompartiendo(false);
    }
  }

  return (
    <div className="space-y-4 text-center">
      <p className="text-label-bold uppercase tracking-wider text-on-surface-variant">
        Listo para compartir
      </p>
      {imagen ? (
        // Fondo blanco fijo: un QR sobre superficie oscura no lo lee ningún
        // escáner, y el tema del teléfono puede estar en oscuro.
        <img
          src={imagen}
          alt="Código QR de la invitación"
          className="mx-auto rounded-xl bg-white p-3"
          width={320}
          height={320}
        />
      ) : (
        <div className="mx-auto h-[320px] w-[320px] animate-pulse rounded-xl bg-surface-container-high" />
      )}
      {invitacion.nombreInvitado && (
        <p className="text-headline-md text-on-surface">{invitacion.nombreInvitado}</p>
      )}
      {resumenRecurrencia(invitacion) && (
        <p className="text-label-bold text-primary">{resumenRecurrencia(invitacion)}</p>
      )}
      <p className="text-body-md text-on-surface-variant">
        Vence el{' '}
        {new Date(invitacion.vigenteHasta).toLocaleString('es-MX', {
          day: 'numeric',
          month: 'long',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </p>
      {aviso && <p className="text-body-md text-on-surface-variant">{aviso}</p>}
      <div className="flex gap-3">
        <Boton
          variante="primario"
          className="flex-1"
          disabled={!imagen || compartiendo}
          onClick={() => void compartir()}
        >
          {compartiendo ? 'Compartiendo…' : 'Compartir'}
        </Boton>
        {alCancelar && (
          <Boton variante="peligro" onClick={alCancelar}>
            Cancelar
          </Boton>
        )}
      </div>
    </div>
  );
}

// ── Cómo quiere que le avisen ─────────────────────────────────

interface Preferencias {
  unidad: string;
  recibeAvisos: boolean;
  canalAviso: 'PUSH' | 'WHATSAPP';
}

/**
 * Que el residente sepa por dónde le va a llegar el aviso.
 *
 * **Ya no se elige nada aquí, y por eso solo queda una frase.** Había dos
 * cosas, y las dos las escondió el cliente:
 *
 *  1. El canal, con dos botones: «En la app» y «WhatsApp». Ahora todo va por
 *     la app.
 *  2. Un botón para dejar de recibir avisos, y otro para volver.
 *
 * **Escondido no es borrado.** El servidor sigue entendiendo `canalAviso` y
 * `recibeAvisos`, el `PATCH` de preferencias sigue existiendo y el relay sigue
 * sabiendo mandar por WhatsApp. Volver a enseñarlo es devolver los botones y
 * el `guardar()` que los acompañaba; está en el historial, en este archivo.
 *
 * **A quién dejaba fuera se comprobó antes de tocarlo**, que es la pregunta
 * que importa al quitar un interruptor: quien lo tuviera apagado se queda sin
 * avisos y sin forma de volver a encenderlos. No había ninguno. Los dos
 * padrones —la nube y el gemelo— tenían `recibeAvisos` en `true` en los nueve
 * residentes, el valor por omisión de la columna ya es `true`, y `canalAviso`
 * nulo se resuelve como PUSH en `NotificacionesService`. Así que esconderlos
 * no movió a nadie y no hizo falta migrar nada.
 *
 * La frase se queda: es el único sitio donde el residente lee que los avisos
 * llegan aquí dentro, y sin ella la app no se lo dice en ninguna parte.
 */
function Avisos() {
  const [pref, setPref] = useState<Preferencias | null>(null);

  useEffect(() => {
    pedir<Preferencias>('/invitaciones/preferencias')
      .then(setPref)
      .catch(() => undefined);
  }, []);

  if (!pref) return null;

  return (
    <Panel>
      <p className="p-5 text-body-md text-on-surface-variant">
        Te avisamos en esta app cuando alguien entra a {pref.unidad}.
      </p>
    </Panel>
  );
}

// ── Avisos de la administración ───────────────────────────────

/**
 * La tarjeta del tablero, con cuántos quedan sin leer.
 *
 * El contador no es adorno: es lo único que distingue «hay algo que la
 * administración quiere que sepas» de una sección más. Sin él, el residente
 * entra a mirar por si acaso o —más probable— no entra nunca.
 */
function TarjetaAvisos({ alAbrir }: { alAbrir: () => void }) {
  const [sinLeer, setSinLeer] = useState(0);

  const contar = useCallback(() => {
    pedir<MensajeParaResidente[]>('/mensajes/mios')
      .then((ms) => setSinLeer(ms.filter((m) => m.leidoEn === null).length))
      // Que falle solo esconde el contador. La sección sigue ahí y se puede
      // abrir: sería peor tapar los avisos porque no se pudo contarlos.
      .catch(() => undefined);
  }, []);

  useEffect(contar, [contar]);
  // Y otra vez cuando llegue uno, o cuando se vuelva a la app: si no, el
  // residente ve el aviso en el teléfono y la tarjeta sigue diciendo lo mismo.
  useAlLlegarAviso(contar);

  // La misma tarjeta que las demás, sin descripción: «Avisos» se explica solo,
  // y lo que hay que mirar aquí es el número.
  return <TarjetaAccion icono="avisos" titulo="Avisos" insignia={sinLeer} onClick={alAbrir} />;
}

/**
 * La lista de avisos.
 *
 * Se marca leído al **abrirlo**, no al recibirlo. Que el teléfono lo haya
 * bajado no significa que la persona lo haya visto, y el contador que ve la
 * administración es lo que le dice si su aviso sirvió de algo.
 */
function AvisosDelResidente() {
  const [avisos, setAvisos] = useState<MensajeParaResidente[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [abierto, setAbierto] = useState<string | null>(null);

  const cargar = useCallback(() => {
    pedir<MensajeParaResidente[]>('/mensajes/mios')
      .then(setAvisos)
      .catch((e) =>
        setError(e instanceof ErrorApi ? e.message : 'No se pudieron cargar los avisos'),
      );
  }, []);

  useEffect(cargar, [cargar]);
  // Estando **en** la lista, un aviso nuevo tiene que aparecer solo. Es la
  // pantalla donde más raro se ve que no lo haga.
  useAlLlegarAviso(cargar);

  function abrir(m: MensajeParaResidente) {
    const cerrar = abierto === m.id;
    setAbierto(cerrar ? null : m.id);
    if (cerrar || m.leidoEn) return;

    const ahora = new Date().toISOString();
    void pedir(`/mensajes/${m.id}/leido`, { metodo: 'POST' }).catch(() => undefined);
    // Se pinta como leído sin esperar al servidor: la marca es informativa y
    // hacer esperar a alguien para tachar un punto azul sería absurdo.
    setAvisos((as) =>
      as === null ? null : as.map((a) => (a.id === m.id ? { ...a, leidoEn: ahora } : a)),
    );
  }

  if (error) return <ErrorCaja>{error}</ErrorCaja>;
  if (!avisos) return <p className="py-8 text-center text-body-md text-outline">Cargando…</p>;

  if (avisos.length === 0) {
    return (
      <p className="py-10 text-center text-body-md text-on-surface-variant">
        No tienes avisos de la administración.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-headline-lg text-on-surface">Avisos</h1>

      {avisos.map((m) => (
        <Panel key={m.id} className="transition-colors hover:bg-surface-container-low">
          <div className="p-5">
            {/*
              **El botón cubre solo la cabecera, no la tarjeta entera.**

              Un `<a>` dentro de un `<button>` es HTML inválido: el navegador lo
              pinta, pero el toque se lo queda el botón y la liga no abre nunca.
              Como el cuerpo del aviso puede traer direcciones, tiene que quedar
              fuera.
            */}
            <button onClick={() => abrir(m)} className="w-full text-left">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <h2 className="text-headline-md text-on-surface">{m.titulo}</h2>
                  <p className="mt-1 text-label-sm text-outline">{cuando(m.publicadoEn)}</p>
                </div>

                {/* Del mismo rojo que el globito del tablero: quien vio un «1»
                    allí tiene que reconocer aquí cuál es. */}
                {m.leidoEn === null && (
                  <span
                    aria-label="Sin leer"
                    className="mt-2 h-3 w-3 shrink-0 rounded-full bg-error"
                  />
                )}
              </div>

              {/* Cerrado, un adelanto de dos líneas. En texto plano porque esto
                  sí vive dentro del botón — y en dos líneas recortadas una liga
                  no serviría de nada. */}
              {abierto !== m.id && (
                <p className="mt-3 line-clamp-2 whitespace-pre-wrap text-body-md text-on-surface-variant">
                  {m.cuerpo}
                </p>
              )}
            </button>

            {abierto === m.id && (
              <>
                <p className="mt-3 whitespace-pre-wrap text-body-md text-on-surface-variant">
                  {conLigas(m.cuerpo)}
                </p>
                {m.adjuntos.map((a) => (
                  <AdjuntoDelAviso key={a.id} adjunto={a} />
                ))}
              </>
            )}

            {abierto !== m.id && m.adjuntos.length > 0 && (
              <p className="mt-3 text-label-sm text-outline">
                {m.adjuntos.length} archivo{m.adjuntos.length === 1 ? '' : 's'} — toca para ver
              </p>
            )}
          </div>
        </Panel>
      ))}
    </div>
  );
}

/**
 * Una imagen o un video del aviso.
 *
 * Se descarga con `fetch` y se pinta desde un blob, igual que las fotos de las
 * visitas: exige sesión, y una etiqueta `<img>` o `<video>` no manda la
 * cabecera de autorización.
 */
function AdjuntoDelAviso({ adjunto }: { adjunto: AdjuntoMensaje }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    let objeto: string | null = null;

    void fetch(`${baseApi()}/api/mensajes/adjuntos/${adjunto.id}`, {
      headers: { Authorization: `Bearer ${almacenToken.leer() ?? ''}` },
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => {
        if (!b || !vivo) return;
        objeto = URL.createObjectURL(b);
        setUrl(objeto);
      })
      .catch(() => undefined);

    return () => {
      vivo = false;
      if (objeto) URL.revokeObjectURL(objeto);
    };
  }, [adjunto.id]);

  if (!url) {
    return <div className="mt-3 h-40 animate-pulse rounded-xl bg-surface-variant" />;
  }

  /*
    **Con tope de alto y `object-contain`.**

    Sin el tope, una imagen alta —una circular escaneada en vertical, un cartel—
    ocupa pantallas enteras y hay que hacer scroll para llegar al siguiente
    aviso. Y `w-full` a secas estira lo que sea a todo el ancho: una imagen
    pequeña o de proporción rara se deforma hasta ser irreconocible.

    Se vio con una de prueba de un píxel, que salió como un rectángulo de color
    del tamaño de media pantalla.
  */
  return adjunto.tipo === 'VIDEO' ? (
    // `controls` y nada más: sin reproducción automática, que en datos móviles
    // gasta sin permiso.
    <video src={url} controls className="mt-3 max-h-96 w-full rounded-xl bg-surface-variant" />
  ) : (
    <img
      src={url}
      alt=""
      className="mt-3 max-h-96 w-full rounded-xl bg-surface-variant object-contain"
    />
  );
}
